import {
	type DayClose as Close,
	calculateDailyAllowance,
	dateAnchor,
	deriveDayKind,
	formatKstDate,
	formatWon,
	type HouseholdConsumptionState,
	kstDateKey,
	resolveLivingBudgetPlan,
} from "@sobidogam/domain";
import type { Receipt } from "@sobidogam/storage";
import { useState } from "react";
import { monthStartDayPref } from "./prefs";
import { Sheet } from "./Sheet";
export function DayStamp({
	kind,
	small = false,
}: {
	kind: Close["kind"];
	small?: boolean;
}) {
	return (
		<svg
			className={`day-stamp ${small ? "small" : ""}`}
			viewBox="0 0 100 100"
			role="img"
			aria-label={
				kind === "zero"
					? "0원 쓴 날"
					: kind === "under"
						? "적게 쓴 날"
						: "마감한 날"
			}
			fill="none"
			stroke="currentColor"
			strokeWidth="3"
		>
			<circle cx="50" cy="50" r="43" />
			<circle cx="50" cy="50" r="36" strokeWidth="1" />
			{kind === "zero" ? (
				<ellipse cx="50" cy="49" rx="12" ry="19" />
			) : kind === "under" ? (
				<path d="m32 49 12 12 24-25" />
			) : (
				<circle cx="50" cy="50" r="7" fill="currentColor" />
			)}
		</svg>
	);
}
export function DayClose({
	date,
	receipts,
	household,
	closes,
	onSave,
	onClose,
	initialSaved = false,
	initialError = "",
}: {
	date: string;
	initialSaved?: boolean;
	initialError?: string;
	receipts: Receipt[];
	household: HouseholdConsumptionState;
	closes: Close[];
	onSave: (close: Close | null, date: string) => Promise<boolean>;
	onClose: () => void;
}) {
	const existing = closes.find((c) => c.date === date);
	const [selected, setSelected] = useState(existing?.homeItemIds ?? []);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState(initialError);
	const [completed, setCompleted] = useState(initialSaved);
	const [notice, setNotice] = useState("");
	const [pendingUndo, setPendingUndo] = useState(false);
	const plan = resolveLivingBudgetPlan(
		household,
		dateAnchor(date),
		monthStartDayPref(),
	);
	const daily = calculateDailyAllowance(
		receipts,
		plan,
		household.recurringPayments,
		dateAnchor(date),
	);
	const kind = deriveDayKind(daily.spent, daily.allowance);
	const label =
		kind === "zero"
			? "0원 쓴 날"
			: kind === "under"
				? "적게 쓴 날"
				: "하루를 마감한 날";
	const save = async (undo = false, homeIds = selected) => {
		setBusy(true);
		setError("");
		setNotice("");
		setPendingUndo(undo);
		const ok = await onSave(
			undo
				? null
				: {
						date,
						spent: daily.spent,
						allowance: daily.allowance,
						kind,
						homeItemIds: homeIds.filter((id) =>
							household.items.some((item) => item.itemId === id),
						),
					},
			date,
		).catch(() => false);
		setBusy(false);
		if (ok) {
			setCompleted(!undo);
			setNotice(
				undo ? "도장을 지웠어요. 결제 기록은 그대로예요." : "도장을 찍었어요.",
			);
		} else
			setError(
				"저장하지 못했어요. 고른 물건은 그대로예요. 저장 공간을 확인하고 다시 눌러 주세요.",
			);
	};
	const future = date > kstDateKey(new Date());
	return (
		<Sheet
			title={`${formatKstDate(dateAnchor(date))} 마감`}
			onClose={onClose}
			footer={
				<button
					className="button full-width"
					type="button"
					disabled={busy || future}
					onClick={() =>
						error ? void save(pendingUndo) : completed ? onClose() : void save()
					}
				>
					{busy
						? "저장 중…"
						: error
							? pendingUndo
								? "도장 되돌리기 다시 시도"
								: "다시 저장하기"
							: completed
								? "오늘로 돌아가기"
								: existing
									? "도장 바꾸기"
									: "도장 찍기"}
				</button>
			}
		>
			<div className="close-preview" data-saved={completed && !error && !busy}>
				{!error && !busy && (notice || completed) ? (
					<p aria-live="polite">{notice || "도장을 찍었어요."}</p>
				) : null}
				<DayStamp kind={kind} />
				<h2>{label}</h2>
				<p>
					{formatWon(daily.spent)} 기록했어요
					{daily.allowance !== null
						? ` · 하루 몫 ${formatWon(daily.allowance)}`
						: ""}
				</p>
			</div>
			<fieldset className="home-item-choices">
				<legend>
					집에 있던 걸 썼나요? <small>선택</small>
				</legend>
				{household.items.length ? (
					household.items.map((item) => (
						<label key={item.itemId}>
							<input
								type="checkbox"
								disabled={busy}
								checked={selected.includes(item.itemId)}
								onChange={(e) => {
									const next = e.target.checked
										? [...selected, item.itemId]
										: selected.filter((id) => id !== item.itemId);
									setSelected(next);
									if (completed) void save(false, next);
								}}
							/>
							{item.name}
						</label>
					))
				) : (
					<p>
						살림에 물건을 등록하면 여기서 고를 수 있어요. 도장만 찍어도 돼요.
					</p>
				)}
			</fieldset>
			<p className="field-hint">
				마감하지 않아도 기록으로 날 수를 세요. 도장은 모양과 집에 있던 물건만
				더해요.
			</p>
			{existing ? (
				<button
					className="quiet-button"
					type="button"
					disabled={busy}
					onClick={() => void save(true)}
				>
					도장 되돌리기 · 결제 기록은 유지
				</button>
			) : null}
			{future ? <p>오늘까지 날짜를 고를 수 있어요.</p> : null}
			{error ? <p role="alert">{error}</p> : null}
		</Sheet>
	);
}
